import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, Crown, Heart, Flower2, Loader2, Sparkles, CreditCard } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { SpeechBubble, MillieHero } from "../components/MascotBubble";
import PlayPaywall from "../components/PlayPaywall";
import { isAndroidApp } from "../lib/playBilling";

const ICON = { free: Flower2, sorted: Heart, plus: Crown };
const TONE = { free: "bg-[#DDEBE1] text-emerald-700", sorted: "bg-[#FCE1E8] text-sorted-rose", plus: "bg-[#FCEFCF] text-amber-600" };

export default function Plans() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [me, setMe] = useState(null);
  const [yearly, setYearly] = useState(false);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    api.get("/billing/plans").then((r) => setData(r.data)).catch(() => toast.error("Couldn't load plans"));
    api.get("/billing/me").then((r) => setMe(r.data)).catch(() => {});
  }, []);

  const choose = async (plan) => {
    setBusy(plan);
    try {
      const { data } = await api.post("/billing/checkout", { plan, interval: yearly ? "year" : "month", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setBusy(null); }
  };

  const choosePaypal = async (plan) => {
    setBusy(`paypal-${plan}`);
    try {
      const { data } = await api.post("/billing/paypal/subscribe", { plan, interval: yearly ? "year" : "month", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); setBusy(null); }
  };

  const manage = async () => {
    if (me?.provider === "paypal") {
      if (!window.confirm("Cancel your PayPal subscription? You'll keep access until the end of the current period.")) return;
      try { const { data } = await api.post("/billing/paypal/cancel"); toast.success(`Cancelled — you're Sorted until ${data.current_period_end ? new Date(data.current_period_end).toLocaleDateString("en-AU") : "the period ends"}`); const r = await api.get("/billing/me"); setMe(r.data); }
      catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
      return;
    }
    try { const { data } = await api.post("/billing/portal", { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center bg-[#FFF6F1]"><Loader2 className="animate-spin text-sorted-rose" /></div>;
  const order = ["free", "sorted", "plus"];
  const android = isAndroidApp();
  const reloadMe = () => api.get("/billing/me").then((r) => setMe(r.data)).catch(() => {});

  return (
    <div className="min-h-screen w-full bg-[#F7E9E6] flex justify-center">
      <div className="relative w-full max-w-md min-h-screen bg-gradient-to-b from-[#FFF6F1] to-[#FCE7EC] pb-16">
        <div className="px-6 pt-12 flex items-center gap-3">
          <button onClick={() => navigate(-1)} data-testid="plans-back" className="w-11 h-11 rounded-full bg-white/80 border-2 border-white flex items-center justify-center text-sorted-ink shadow-soft"><ArrowLeft size={19} strokeWidth={2.5} /></button>
          <h1 className="font-hand text-4xl text-sorted-ink">Go Sorted</h1>
        </div>

        <div className="relative h-40 mt-1">
          <MillieHero mood="celebrate" className="absolute right-0 bottom-0 h-40 w-[42%]" testId="plans-millie" />
          <div className="absolute left-6 top-2 max-w-[56%]">
            <SpeechBubble tail="br" testId="plans-bubble">
              {me?.tier > 0 ? `You're on ${me.name} — thank you for keeping the lights on in my kitchen!` : "Free is properly useful. Sorted is me, full-time, every night of the week."}
            </SpeechBubble>
          </div>
        </div>

        {data.beta_unlocked && (
          <p className="mx-6 text-[11px] font-bold text-sorted-ink bg-white/80 border-2 border-white rounded-2xl px-3 py-2 flex items-center gap-1.5" data-testid="plans-beta-note">
            <Sparkles size={13} strokeWidth={2.5} className="text-sorted-rose" /> Beta: everything's unlocked for everyone right now. Subscribing early locks in your price.
          </p>
        )}

        {android ? (
          <>
            <PlayPaywall me={me} onChanged={reloadMe} />
            <p className="px-6 mt-3 text-[11px] text-center text-sorted-slate font-semibold" data-testid="plans-legal">
              <Link to="/terms" data-testid="plans-terms" className="underline underline-offset-2">Terms</Link> · <Link to="/privacy" data-testid="plans-privacy" className="underline underline-offset-2">Privacy</Link>
            </p>
          </>
        ) : (<>
        <div className="mx-6 mt-4 bg-white rounded-full p-1 flex border-2 border-[#FDECEF]" data-testid="plans-interval">
          {[["month", "Monthly"], ["year", "Yearly · save 33%"]].map(([k, l]) => (
            <button key={k} onClick={() => setYearly(k === "year")} data-testid={`plans-interval-${k}`}
              className={`flex-1 py-2 rounded-full text-xs font-bold ${(k === "year") === yearly ? "bg-sorted-ink text-white" : "text-sorted-slate"}`}>{l}</button>
          ))}
        </div>

        <div className="px-6 mt-4 space-y-3">
          {order.map((key) => {
            const p = data.plans[key];
            const Icon = ICON[key];
            const price = yearly ? p.yearly : p.monthly;
            const current = me?.plan === key;
            return (
              <div key={key} className={`bg-white rounded-3xl border-2 p-4 shadow-soft ${key === "sorted" ? "border-sorted-rose" : "border-[#FDECEF]"}`} data-testid={`plan-${key}`}>
                <div className="flex items-center gap-3">
                  <span className={`w-11 h-11 rounded-2xl flex items-center justify-center ${TONE[key]}`}><Icon size={20} strokeWidth={2.5} /></span>
                  <div className="flex-1">
                    <p className="font-heading font-semibold text-sorted-ink leading-tight">{p.name} {key === "sorted" && <span className="ml-1 text-[10px] font-bold bg-sorted-rose text-white px-2 py-0.5 rounded-full">Most loved</span>}</p>
                    <p className="text-[11px] text-sorted-slate font-semibold">{p.blurb}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-heading font-semibold text-sorted-ink text-lg leading-none">{price ? `${data.currency}${price.toFixed(2)}` : "Free"}</p>
                    {price ? <p className="text-[10px] font-bold text-sorted-slate">/{yearly ? "year" : "month"}</p> : null}
                  </div>
                </div>
                {key !== "free" && (
                  current ? (
                    <button onClick={manage} data-testid={`plan-manage-${key}`} className="mt-3 w-full py-3 rounded-full border-2 border-[#FDECEF] text-sm font-bold text-sorted-ink flex items-center justify-center gap-1"><Check size={15} strokeWidth={3} /> Your plan · {me?.provider === "paypal" ? "Cancel PayPal subscription" : "Manage billing"}</button>
                  ) : (
                    <>
                      <button onClick={() => choose(key)} disabled={!!busy} data-testid={`plan-choose-${key}`}
                        className={`mt-3 w-full py-3 rounded-full text-sm font-bold flex items-center justify-center gap-1 ${key === "sorted" ? "bg-sorted-rose text-white" : "bg-sorted-ink text-white"} disabled:opacity-60`}>
                        {busy === key ? <Loader2 size={15} className="animate-spin" /> : <CreditCard size={15} strokeWidth={2.5} />} {me?.tier > 0 ? `Switch to ${p.name}` : `Start ${data.trial_days}-day free trial`} · card
                      </button>
                      {data.paypal_enabled && (
                        <button onClick={() => choosePaypal(key)} disabled={!!busy} data-testid={`plan-paypal-${key}`}
                          className="mt-2 w-full py-3 rounded-full text-sm font-bold flex items-center justify-center gap-1.5 bg-[#FFC439] text-[#003087] disabled:opacity-60">
                          {busy === `paypal-${key}` ? <Loader2 size={15} className="animate-spin" /> : null}
                          <span className="italic font-extrabold tracking-tight"><span className="text-[#003087]">Pay</span><span className="text-[#0070BA]">Pal</span></span> {me?.tier > 0 ? "" : `· ${data.trial_days}-day free trial`}
                        </button>
                      )}
                    </>
                  )
                )}
                {key === "free" && current && <p className="mt-2 text-[11px] font-bold text-emerald-700 flex items-center gap-1"><Check size={12} strokeWidth={3} /> Your current plan</p>}
              </div>
            );
          })}
        </div>

        <p className="px-6 mt-3 text-[11px] text-center text-sorted-slate font-semibold" data-testid="plans-legal">
          Subscriptions renew automatically; cancel anytime. <Link to="/terms" data-testid="plans-terms" className="underline underline-offset-2">Terms</Link> · <Link to="/privacy" data-testid="plans-privacy" className="underline underline-offset-2">Privacy</Link>
        </p>

        </>)}

        <div className="px-6 mt-6">
          <h3 className="font-hand text-2xl text-sorted-ink mb-2">What's in each</h3>
          <div className="bg-white rounded-3xl border-2 border-[#FDECEF] overflow-hidden shadow-soft" data-testid="plans-features">
            <div className="grid grid-cols-[1.6fr_1fr_1fr_1fr] text-[10px] font-extrabold text-sorted-slate uppercase tracking-wide px-3 py-2 bg-[#FFF6F1]">
              <span>Feature</span><span className="text-center">Free</span><span className="text-center">Sorted</span><span className="text-center">Sorted+</span>
            </div>
            {data.features.map(([f, a, b, c], i) => (
              <div key={f} className={`grid grid-cols-[1.6fr_1fr_1fr_1fr] px-3 py-2 text-[11px] ${i % 2 ? "bg-[#FFFBF9]" : ""}`}>
                <span className="font-semibold text-sorted-ink">{f}</span>
                {[a, b, c].map((v, j) => <span key={j} className={`text-center font-bold ${v === "✓" ? "text-emerald-600" : v === "—" ? "text-sorted-slate/50" : "text-sorted-slate"}`}>{v}</span>)}
              </div>
            ))}
          </div>
          <p className="text-[11px] text-sorted-slate mt-3 text-center">{android ? "Prices in AUD, billed by Google Play. Cancel anytime in Google Play → Subscriptions." : "Prices in AUD, GST handled at checkout. Cancel anytime from Manage billing."}</p>
        </div>
      </div>
    </div>
  );
}
